/**
 * VIP 11 – New Case Form (11NewCase.js)
 * ------------------------------------------------------------
 * 특이사항 & 구현 규칙 (2024-06-30 작성)
 * 1. Date-Time 입력 방식
 *    • HTML5 의 <input type="datetime-local"> 는 사용하지 않는다.
 *    • 대신 <input type="text"> 로 고정하고, 포맷은 "YYYY-MM-DD HH:MM".
 *    • 이유: 사용자가 값을 복사/붙여넣기 하기 쉽도록 함.
 *    • 화면 표시에만 'T' → 공백 치환하여 보여주며, 저장 값은 그대로 유지.
 *
 * 2. hospital 필드 자동 매핑 & 잠금
 *    • /edc/user-value-check Lambda 로부터 user_value(정수) 를 가져온다.
 *    • user_value 의 10의 자리(1~9)를 알파벳(A~I)에 매핑 (10→A, 20→B … 90→I).
 *    • 매핑된 병원 코드가 formData.hospital 에 자동 세팅된다.
 *    • UI 에서 병원 라디오는 disabled 처리하여 사용자가 수정 불가.
 *
 * 3. user_value 조회
 *    • formAPI.getUserValue(projectId) 호출.
 *    • 토큰은 헤더 + body 모두 전달해 Lambda 에서 인증.
 *
 * 4. 기타
 *    • 총 8 Step Wizard 구조 (Initial, ICU, Daily … Outcome).
 *    • 추후 필드 추가/조건부 렌더링 시 이 헤더 주석에 변경사항을 기록할 것.
 *
 * 5. GCS 자동 합계 계산 (arrival_gcs_total)
 *    • Eye / Verbal / Motor 라디오 value 는 "점수 : 설명" 형식(예: "3 : To speech")
 *    • useEffect 로 E·V·M 변경 시 총합 계산해 arrival_gcs_total 에 저장
 *      – E 또는 M 점수가 0 ➜ 총점 0 (Untestable, Unknown 등)
 *      – V 점수가 0 일 때는 (E+M)의 합에 아래 보정치를 가산
 *         · 합 ≤6 → +1, 합=7 → +2, 합 8~9 → +4, 합=10 → +5
 *      – 그 외에는 E + V + M 단순 합산
 *    • 초기값은 ""(null) 상태로 두고 선택 완료 시 숫자 문자열로 채움
 *
 * 6. SAH 동맥류 자동 계산 로직
 *    • Aspect Ratio 자동 계산 (aneurysm_aspect_ratio)
 *      – 공식: height / neck_max
 *      – aneurysm_height, aneurysm_neck_max 변경시 실시간 계산
 *      – 소수점 2자리까지 표시, neck이 0이거나 비어있으면 빈값 처리
 *    • Bottleneck Factor 자동 계산 (aneurysm_bottleneck_factor)
 *      – 공식: width / neck_max
 *      – aneurysm_width, aneurysm_neck_max 변경시 실시간 계산
 *      – 소수점 2자리까지 표시, neck이 0이거나 비어있으면 빈값 처리
 *    • ns-form.html의 JavaScript 로직을 React useEffect로 구현
 *
 * 7. TBI 자동 계산 로직
 *    • ISS Score 자동 계산 (tbi_iss_score)
 *      – 공식: ISS = (상위 3개 AIS 점수)² 합계
 *      – ISS 관련 6개 필드 (headneck, face, chest, abdomen, external, extremities) 기반
 *      – issOptions 배열의 point 키를 이용하여 계산 (0-6점)
 *      – 0점(No Injury)은 제외하고 상위 3개 점수의 제곱합으로 ISS 계산
 *      – useEffect로 실시간 계산하여 readOnly 필드에 자동 입력
 *    • Rotterdam CT Score 자동 계산 (tbi_rotterdam)
 *      – 공식: 1 + 기저조(0-2) + 중선편위(0-1) + EDH(0-1) + 허혈성병변(0-1)
 *      – rotterdamOptions 객체의 point 키를 이용하여 계산
 *      – 기저조 압박(cistern), 중선편위(midlineShift), EDH, 허혈성병변(ischemia) 기반
 *      – 총 점수 범위: 1-6점, useEffect로 실시간 계산
 *
 * 8. ICH Score 자동 계산 로직
 *    • ICH Score 자동 계산 (ich_score)
 *      – 공식: GCS(0-2) + ICH volume(0-1) + IVH(0-1) + Infratentorial(0-1) + Age(0-1)
 *      – ichScoreOptions 객체의 point 키를 이용하여 계산
 *      – 5개 필드 모두 선택되었을 때만 계산, 총 점수 범위: 0-6점
 *      – useEffect로 실시간 계산하여 readOnly 필드에 자동 입력
 *    • Modified Graeb Scale 자동 계산 (ivh_mgraeb_score)
 *      – 공식: 16개 IVH 관련 필드의 점수 합산 (ns-form.html 로직 정확 구현)
 *      – 필드 구성:
 *         · 우측: temporal tip + expansion, lateral ventricle + expansion, occipital tip + expansion (6개)
 *         · 좌측: temporal tip + expansion, lateral ventricle + expansion, occipital tip + expansion (6개)
 *         · 중앙: 3rd ventricle + expansion, 4th ventricle + expansion (4개)
 *      – 점수 체계:
 *         · basic (temporal/occipital tip): 0=None, 1=≤50%, 2=50-100%
 *         · lateral (좌우 lateral ventricle): 0=None, 1=≤25%, 2=25-50%, 3=50-75%, 4=75-100%
 *         · expansion (모든 *_exp 필드): 0=No, 1=Expanded
 *         · 3rd/4th ventricle basic: 0=None, 1=≤50%, 2=50-100%
 *      – 특별 계산 규칙 (ns-form.html 로직):
 *         · ivh_mgraeb_3rd: 점수 × 2
 *         · ivh_mgraeb_4th: 점수 × 2
 *         · 나머지 모든 필드 (expansion 포함): 점수 × 1
 *      – 총 점수 범위: 0-30점
 *      – 모든 16개 필드 선택 시에만 자동 계산, useEffect로 실시간 업데이트
 *
 * 9. 조건부 필드 렌더링 시스템
 *    • index_injury 값에 따른 동적 섹션 표시
 *      – "SAH (ruptured aneurysm)": SAH 전용 필드 30여개
 *      – "TBI": TBI 전용 필드 22개
 *      – "ICH (primary, secondary) / IVH": ICH/IVH 필드 35여개
 *      – "Ischemic stroke": 허혈성 뇌졸중 필드 (미구현)
 *      – "Others": 기타 필드 (미구현)
 *    • getInjurySection() 함수로 중앙화된 조건부 렌더링 관리
 *
 * 10. ICU 페이지 자동 계산 로직 (2페이지)
 *    • ICP Monitor Duration 자동 계산 (icp_duration)
 *      – 공식: icp_removal_dt - icp_ins_dt (분 단위)
 *      – icp_ins_dt, icp_removal_dt 변경시 실시간 계산
 *      – 시간 차이를 분(minutes) 단위로 표시
 *    • TTM Duration 자동 계산 (ttm_duration)
 *      – 공식: ttm_end_dt - ttm_start_dt (분 단위)
 *      – ttm_start_dt, ttm_end_dt 변경시 실시간 계산
 *      – 시간 차이를 분(minutes) 단위로 표시
 *    • calculateTimeDifference 함수로 공통 시간 차이 계산 로직 구현
 *
 * 11. 하단 고정 패널 시스템
 *    • "Status 체크" 패널로 실시간 상태 모니터링
 *    • initial_status, icu_status 필드값 실시간 표시
 *    • 패널 토글 기능 (확장/축소)
 *    • 스크롤 위치에 따른 맨 위로 이동 버튼
 *    • 반응형 디자인 적용
 * ------------------------------------------------------------
 */
import React, { useState, useEffect, useRef } from "react";
import { FiChevronLeft, FiChevronRight, FiSave, FiX } from "react-icons/fi";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { formAPI } from "../../../../../services/api";
import InitialPage from "./pages/InitialPage";
import ICUPage from "./pages/ICUPage";
import DailyPage from "./pages/DailyPage";
import AddDailyPage from "./pages/AddDailyPage";
import EarlyOutcome from "./pages/EarlyOutcome";
import HospDischarge from "./pages/HospDischarge";
import Surgeries from "./pages/Surgeries";
import Outcome from "./pages/Outcome";
import "./11NewCase.css";

// 전체 단계 개수 (Progress 버튼에 맞춰 9단계로 수정)
const totalSteps = 9;

// 단계 라벨 (Progress 버튼 텍스트와 동일하게 유지)
const stepLabels = [
  "Initial",
  "ICU",
  "Daily",
  "Additional Daily",
  "Early Outcome",
  "Hosp Discharge",
  "Surgeries",
  "Outcome",
];

const VIP11Edit = ({ projectId, initialFormData }) => {
  const navigate = useNavigate();
  const { dataId } = useParams();
  const location = useLocation();

  // 디버깅을 위한 즉시 로깅
  console.group("[VIP11Edit] Initial Load Debug");
  console.log("Props:", { projectId, initialFormData });
  console.log("Location State:", location.state);
  console.log("DataId:", dataId);
  console.groupEnd();

  // 상태 관리
  const [currentStep, setCurrentStep] = useState(1);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [userValue, setUserValue] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSavePopover, setShowSavePopover] = useState(false);

  // formData 초기화 - props의 initialFormData 사용
  const [formData, setFormData] = useState(() => {
    console.group("[VIP11Edit] FormData Initialization");
    console.log("Initial Form Data from props:", initialFormData);

    if (initialFormData) {
      console.log("Using initialFormData from props");
      const initialData = { ...initialFormData };
      console.log("Initialized FormData:", initialData);
      console.groupEnd();
      return initialData;
    }
    console.log("No initialFormData, returning null");
    console.groupEnd();
    return null;
  });

  // formData 상태 변경 감지를 위한 useEffect
  useEffect(() => {
    console.log("[VIP11Edit] Current formData state:", formData);
  }, [formData]);

  // 컴포넌트 마운트 시 로깅
  useEffect(() => {
    console.group("[VIP11Edit] Component Mount Effect");
    console.log("Initial Props:", { dataId, projectId });
    console.log("Location State:", location.state);
    console.log("Current FormData:", formData);

    if (!location.state?.formData) {
      console.log("No FormData in state, fetching from API");
      fetchFormData();
    } else {
      console.log("Using FormData from state");
    }
    console.groupEnd();
  }, []);

  // API를 통한 데이터 조회 함수
  const fetchFormData = async () => {
    if (!dataId) {
      setError("데이터 ID가 없습니다.");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      console.log("[VIP11Edit] Fetching data from API for dataId:", dataId);

      const response = await formAPI.getFormDataById(dataId);

      if (!response || !response.data) {
        throw new Error("데이터를 불러올 수 없습니다.");
      }

      // 날짜 형식 변환 (T → 공백)
      const processedData = Object.entries(response.data).reduce(
        (acc, [key, value]) => {
          if (typeof value === "string" && value.includes("T")) {
            acc[key] = value.replace("T", " ");
          } else {
            acc[key] = value;
          }
          return acc;
        },
        {},
      );

      console.log("[VIP11Edit] Setting form data:", processedData);
      setFormData(processedData);
    } catch (error) {
      console.error("[VIP11Edit] Error fetching data:", error);
      setError(error.message || "데이터를 불러오는 중 오류가 발생했습니다.");
      setSaveMessage({
        type: "error",
        text: "데이터 불러오기 실패: " + (error.message || "알 수 없는 오류"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 에러 발생 시 표시할 컴포넌트
  const ErrorComponent = () => (
    <div
      className="error-container"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 80px)", // ECRFPage 헤더 높이를 고려
        padding: "20px",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff3f3",
          border: "1px solid #ffcdd2",
          borderRadius: "8px",
          padding: "20px",
          maxWidth: "500px",
        }}
      >
        <h2 style={{ color: "#d32f2f", marginBottom: "15px" }}>오류 발생</h2>
        <p style={{ color: "#555", marginBottom: "20px" }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: "#d32f2f",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          페이지 새로고침
        </button>
      </div>
    </div>
  );

  // 로딩 중 표시할 컴포넌트
  const LoadingComponent = () => (
    <div
      className="loading-container"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 80px)", // ECRFPage 헤더 높이를 고려
        padding: "20px",
        backgroundColor: "#fff",
      }}
    >
      <div
        className="loading-spinner"
        style={{
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #3498db",
          borderRadius: "50%",
          width: "50px",
          height: "50px",
          animation: "spin 1s linear infinite",
          marginBottom: "20px",
        }}
      ></div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <p style={{ color: "#666", fontSize: "16px" }}>
        데이터를 불러오는 중입니다...
      </p>
    </div>
  );

  // formData가 업데이트될 때마다 로깅
  useEffect(() => {
    console.log("[VIP11Edit] Current formData:", formData);
  }, [formData]);

  // 로딩 중이거나 formData가 없으면 로딩 컴포넌트 표시
  if (isLoading || !formData) {
    return <LoadingComponent />;
  }

  // 에러가 있으면 에러 컴포넌트 표시
  if (error) {
    return <ErrorComponent />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.group("[VIP11Edit] HandleChange");
    console.log("Field Name:", name);
    console.log("New Value:", value);
    console.log("Current FormData:", formData);
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      console.log("Updated FormData:", newData);
      console.groupEnd();
      return newData;
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    console.group("[VIP11Edit] HandleCheckboxChange");
    console.log("Field Name:", name);
    console.log("Value:", value);
    console.log("Checked:", checked);
    console.log("Current FormData:", formData);

    setFormData((prev) => {
      const prevArr = Array.isArray(prev[name]) ? prev[name] : [];
      let newArr;
      if (checked) {
        newArr = prevArr.includes(value) ? prevArr : [...prevArr, value];
      } else {
        newArr = prevArr.filter((v) => v !== value);
      }
      const newData = { ...prev, [name]: newArr };
      console.log("Updated FormData:", newData);
      console.groupEnd();
      return newData;
    });
  };

  const dtDisplay = (v) => (typeof v === "string" ? v.replace("T", " ") : v);

  // user_value 조회 시 로깅 추가
  useEffect(() => {
    if (!projectId) {
      console.warn("[VIP11Edit] No projectId provided for userValue fetch");
      return;
    }

    (async () => {
      try {
        console.log("[VIP11Edit] Fetching userValue for projectId:", projectId);
        const res = await formAPI.getUserValue(projectId);
        const val = res.userValue ?? res.user_value ?? null;
        console.log("[VIP11Edit] userValue fetched:", val);
        setUserValue(val);
      } catch (err) {
        console.error("[VIP11Edit] userValue fetch error:", err);
      }
    })();
  }, [projectId]);

  // userValue → hospital 코드 매핑 (관리자는 제외)
  useEffect(() => {
    if (userValue == null) return;

    // 관리자(999)는 hospital 필드를 자동 설정하지 않음
    if (userValue === 999) return;

    const tens = Math.floor(userValue / 10);
    if (tens >= 1 && tens <= 9) {
      const letter = String.fromCharCode(64 + tens);
      setFormData((p) =>
        p.hospital === letter ? p : { ...p, hospital: letter },
      );
    }
  }, [userValue]);

  // GCS 자동 합계 계산 (arrival_gcs_total)
  useEffect(() => {
    const { arrival_gcs_e, arrival_gcs_v, arrival_gcs_m } = formData;

    if (!arrival_gcs_e || !arrival_gcs_v || !arrival_gcs_m) {
      setFormData((p) => ({ ...p, arrival_gcs_total: "" }));
      return;
    }

    // 점수 추출 (예: "3 : To speech" → 3)
    const getScore = (value) => {
      const match = value.match(/^(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    const eScore = getScore(arrival_gcs_e);
    const vScore = getScore(arrival_gcs_v);
    const mScore = getScore(arrival_gcs_m);

    let total;
    if (eScore === 0 || mScore === 0) {
      total = 0; // E 또는 M이 0이면 총점 0
    } else if (vScore === 0) {
      // V가 0일 때 보정 로직
      const emSum = eScore + mScore;
      if (emSum <= 6) total = emSum + 1;
      else if (emSum === 7) total = emSum + 2;
      else if (emSum >= 8 && emSum <= 9) total = emSum + 4;
      else if (emSum === 10) total = emSum + 5;
      else total = emSum;
    } else {
      total = eScore + vScore + mScore;
    }

    setFormData((p) => ({ ...p, arrival_gcs_total: total.toString() }));
  }, [formData.arrival_gcs_e, formData.arrival_gcs_v, formData.arrival_gcs_m]);

  // ICU 입실시 GCS 자동 계산
  useEffect(() => {
    const { icu_adm_gcs_e, icu_adm_gcs_v, icu_adm_gcs_m } = formData;

    if (!icu_adm_gcs_e || !icu_adm_gcs_v || !icu_adm_gcs_m) {
      setFormData((p) => ({ ...p, icu_adm_gcs_total: "" }));
      return;
    }

    const getScore = (value) => {
      const match = value.match(/^(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    const eScore = getScore(icu_adm_gcs_e);
    const vScore = getScore(icu_adm_gcs_v);
    const mScore = getScore(icu_adm_gcs_m);

    let total;
    if (eScore === 0 || mScore === 0) {
      total = 0;
    } else if (vScore === 0) {
      const emSum = eScore + mScore;
      if (emSum <= 6) total = emSum + 1;
      else if (emSum === 7) total = emSum + 2;
      else if (emSum >= 8 && emSum <= 9) total = emSum + 4;
      else if (emSum === 10) total = emSum + 5;
      else total = emSum;
    } else {
      total = eScore + vScore + mScore;
    }

    setFormData((p) => ({ ...p, icu_adm_gcs_total: total.toString() }));
  }, [formData.icu_adm_gcs_e, formData.icu_adm_gcs_v, formData.icu_adm_gcs_m]);

  // ICU 퇴실시 GCS 자동 계산
  useEffect(() => {
    const { icu_dis_gcs_e, icu_dis_gcs_v, icu_dis_gcs_m } = formData;

    if (!icu_dis_gcs_e || !icu_dis_gcs_v || !icu_dis_gcs_m) {
      setFormData((p) => ({ ...p, icu_dis_gcs_total: "" }));
      return;
    }

    const getScore = (value) => {
      const match = value.match(/^(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    const eScore = getScore(icu_dis_gcs_e);
    const vScore = getScore(icu_dis_gcs_v);
    const mScore = getScore(icu_dis_gcs_m);

    let total;
    if (eScore === 0 || mScore === 0) {
      total = 0;
    } else if (vScore === 0) {
      const emSum = eScore + mScore;
      if (emSum <= 6) total = emSum + 1;
      else if (emSum === 7) total = emSum + 2;
      else if (emSum >= 8 && emSum <= 9) total = emSum + 4;
      else if (emSum === 10) total = emSum + 5;
      else total = emSum;
    } else {
      total = eScore + vScore + mScore;
    }

    setFormData((p) => ({ ...p, icu_dis_gcs_total: total.toString() }));
  }, [formData.icu_dis_gcs_e, formData.icu_dis_gcs_v, formData.icu_dis_gcs_m]);

  // SAH 동맥류 Aspect Ratio 자동 계산
  useEffect(() => {
    const { aneurysm_height, aneurysm_neck_max } = formData;

    if (
      !aneurysm_height ||
      !aneurysm_neck_max ||
      parseFloat(aneurysm_neck_max) === 0
    ) {
      setFormData((p) => ({ ...p, aneurysm_aspect_ratio: "" }));
      return;
    }

    const height = parseFloat(aneurysm_height);
    const neck = parseFloat(aneurysm_neck_max);

    if (!isNaN(height) && !isNaN(neck) && neck !== 0) {
      const ratio = (height / neck).toFixed(2);
      setFormData((p) => ({ ...p, aneurysm_aspect_ratio: ratio }));
    }
  }, [formData.aneurysm_height, formData.aneurysm_neck_max]);

  // SAH 동맥류 Bottleneck Factor 자동 계산
  useEffect(() => {
    const { aneurysm_width, aneurysm_neck_max } = formData;

    if (
      !aneurysm_width ||
      !aneurysm_neck_max ||
      parseFloat(aneurysm_neck_max) === 0
    ) {
      setFormData((p) => ({ ...p, aneurysm_bottleneck_factor: "" }));
      return;
    }

    const width = parseFloat(aneurysm_width);
    const neck = parseFloat(aneurysm_neck_max);

    if (!isNaN(width) && !isNaN(neck) && neck !== 0) {
      const factor = (width / neck).toFixed(2);
      setFormData((p) => ({ ...p, aneurysm_bottleneck_factor: factor }));
    }
  }, [formData.aneurysm_width, formData.aneurysm_neck_max]);

  // TBI ISS Score 자동 계산 (ns-form.html 로직 적용)
  useEffect(() => {
    const issFields = [
      "tbi_iss_headneck",
      "tbi_iss_face",
      "tbi_iss_chest",
      "tbi_iss_abdomen",
      "tbi_iss_external",
      "tbi_iss_extremities",
    ];

    const scores = issFields.map((field) => {
      const value = formData[field];
      if (!value) return null;
      const match = value.match(/^(\d+)/);
      return match ? parseInt(match[1]) : 0;
    });

    if (scores.some((score) => score === null)) {
      setFormData((p) => ({ ...p, tbi_iss_score: "" }));
      return;
    }

    // ns-form.html 로직: 6점(Unsurvivable)이 있으면 75점
    if (scores.some((score) => score === 6)) {
      setFormData((p) => ({ ...p, tbi_iss_score: "75" }));
      return;
    }

    // 그렇지 않으면 최고점의 제곱 + 두번째 높은 점수의 제곱
    const sortedScores = scores.sort((a, b) => b - a);
    const maxScore = sortedScores[0];

    // 두번째 높은 점수 찾기 (getMinValueOfTopTwo 함수 구현)
    const getMinValueOfTopTwo = (array) => {
      const sorted = [...array].sort((a, b) => b - a);
      return sorted[1];
    };

    const secondHighest = getMinValueOfTopTwo(scores);
    const issScore = Math.pow(maxScore, 2) + Math.pow(secondHighest, 2);

    setFormData((p) => ({ ...p, tbi_iss_score: issScore.toString() }));
  }, [
    formData.tbi_iss_headneck,
    formData.tbi_iss_face,
    formData.tbi_iss_chest,
    formData.tbi_iss_abdomen,
    formData.tbi_iss_external,
    formData.tbi_iss_extremities,
  ]);

  // TBI Rotterdam CT Score 자동 계산 (ns-form.html 로직 적용)
  useEffect(() => {
    const {
      tbi_cistern_comp,
      tbi_midline_shift_mm,
      tbi_edh,
      tbi_sah,
      tbi_ivh,
    } = formData;

    // 모든 필드가 선택되지 않으면 빈값
    if (
      !tbi_cistern_comp ||
      !tbi_edh ||
      !tbi_sah ||
      !tbi_ivh ||
      tbi_midline_shift_mm === undefined
    ) {
      setFormData((p) => ({ ...p, tbi_rotterdam: "" }));
      return;
    }

    // 점수 추출 함수 (라디오 버튼 값에서 점수 추출)
    const getScore = (value) => {
      if (!value) return 0;
      const match = value.match(/^(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    // 각 필드 점수 계산
    const tbi_comp_score = getScore(tbi_cistern_comp); // 0=Normal, 1=Compressed, 2=Absent
    const tbi_edh_score = getScore(tbi_edh); // 0=Absent, 1=Present
    const tbi_sah_score = getScore(tbi_sah); // 0=Absent, 1=Present
    const tbi_ivh_score = getScore(tbi_ivh); // 0=Absent, 1=Present
    const tbi_shift_value = parseFloat(tbi_midline_shift_mm) || 0; // mm 수치

    // ns-form.html 로직 정확히 구현
    const rotterdamScore =
      1 +
      tbi_comp_score +
      (tbi_shift_value > 5 ? 1 : 0) +
      (tbi_edh_score === 0 ? 1 : 0) +
      (tbi_sah_score === 1 || tbi_ivh_score === 1 ? 1 : 0);

    setFormData((p) => ({ ...p, tbi_rotterdam: rotterdamScore.toString() }));
  }, [
    formData.tbi_cistern_comp,
    formData.tbi_midline_shift_mm,
    formData.tbi_edh,
    formData.tbi_sah,
    formData.tbi_ivh,
  ]);

  // ICH Score 자동 계산
  useEffect(() => {
    const {
      ich_score_gcs,
      ich_score_ich,
      ich_score_ivh,
      ich_score_infratentorial,
      ich_score_age,
    } = formData;

    if (
      !ich_score_gcs ||
      !ich_score_ich ||
      !ich_score_ivh ||
      !ich_score_infratentorial ||
      !ich_score_age
    ) {
      setFormData((p) => ({ ...p, ich_score: "" }));
      return;
    }

    const getScore = (value) => {
      const match = value.match(/^(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    const gcsScore = getScore(ich_score_gcs);
    const ichScore = getScore(ich_score_ich);
    const ivhScore = getScore(ich_score_ivh);
    const infraScore = getScore(ich_score_infratentorial);
    const ageScore = getScore(ich_score_age);

    const totalScore = gcsScore + ichScore + ivhScore + infraScore + ageScore;
    setFormData((p) => ({ ...p, ich_score: totalScore.toString() }));
  }, [
    formData.ich_score_gcs,
    formData.ich_score_ich,
    formData.ich_score_ivh,
    formData.ich_score_infratentorial,
    formData.ich_score_age,
  ]);

  // Modified Graeb Scale 자동 계산 (ns-form.html 로직 정확 구현)
  useEffect(() => {
    const mgraebFields = [
      "ivh_mgraeb_rt_temp",
      "ivh_mgraeb_rt_temp_exp",
      "ivh_mgraeb_rt_lateral",
      "ivh_mgraeb_rt_lateral_exp",
      "ivh_mgraeb_rt_occip",
      "ivh_mgraeb_rt_occip_exp",
      "ivh_mgraeb_lt_temp",
      "ivh_mgraeb_lt_temp_exp",
      "ivh_mgraeb_lt_lateral",
      "ivh_mgraeb_lt_lateral_exp",
      "ivh_mgraeb_lt_occip",
      "ivh_mgraeb_lt_occip_exp",
      "ivh_mgraeb_3rd",
      "ivh_mgraeb_3rd_exp",
      "ivh_mgraeb_4th",
      "ivh_mgraeb_4th_exp",
    ];

    const allFieldsSelected = mgraebFields.every((field) => formData[field]);

    if (!allFieldsSelected) {
      setFormData((p) => ({ ...p, ivh_mgraeb_score: "" }));
      return;
    }

    const getScore = (value) => {
      const match = value.match(/^(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    // ns-form.html 로직: ivh_mgraeb_3rd와 ivh_mgraeb_4th만 점수 × 2
    const totalScore = mgraebFields.reduce((sum, field) => {
      const score = getScore(formData[field]);
      if (field === "ivh_mgraeb_3rd" || field === "ivh_mgraeb_4th") {
        return sum + score * 2;
      }
      return sum + score;
    }, 0);

    setFormData((p) => ({ ...p, ivh_mgraeb_score: totalScore.toString() }));
  }, [
    formData.ivh_mgraeb_rt_temp,
    formData.ivh_mgraeb_rt_temp_exp,
    formData.ivh_mgraeb_rt_lateral,
    formData.ivh_mgraeb_rt_lateral_exp,
    formData.ivh_mgraeb_rt_occip,
    formData.ivh_mgraeb_rt_occip_exp,
    formData.ivh_mgraeb_lt_temp,
    formData.ivh_mgraeb_lt_temp_exp,
    formData.ivh_mgraeb_lt_lateral,
    formData.ivh_mgraeb_lt_lateral_exp,
    formData.ivh_mgraeb_lt_occip,
    formData.ivh_mgraeb_lt_occip_exp,
    formData.ivh_mgraeb_3rd,
    formData.ivh_mgraeb_3rd_exp,
    formData.ivh_mgraeb_4th,
    formData.ivh_mgraeb_4th_exp,
  ]);

  // ICP Monitor Duration 자동 계산
  useEffect(() => {
    const { icp_ins_dt, icp_removal_dt } = formData;

    if (!icp_ins_dt || !icp_removal_dt) {
      setFormData((p) => ({ ...p, icp_duration: "" }));
      return;
    }

    try {
      const start = new Date(icp_ins_dt.replace(" ", "T"));
      const end = new Date(icp_removal_dt.replace(" ", "T"));
      const diffMs = end - start;
      const diffMins = Math.round(diffMs / (1000 * 60));

      if (!isNaN(diffMins) && diffMins >= 0) {
        setFormData((p) => ({ ...p, icp_duration: diffMins.toString() }));
      }
    } catch (e) {
      console.warn("ICP duration 계산 오류:", e);
    }
  }, [formData.icp_ins_dt, formData.icp_removal_dt]);

  // TTM Duration 자동 계산
  useEffect(() => {
    const { ttm_start_dt, ttm_end_dt } = formData;

    if (!ttm_start_dt || !ttm_end_dt) {
      setFormData((p) => ({ ...p, ttm_duration: "" }));
      return;
    }

    try {
      const start = new Date(ttm_start_dt.replace(" ", "T"));
      const end = new Date(ttm_end_dt.replace(" ", "T"));
      const diffMs = end - start;
      const diffMins = Math.round(diffMs / (1000 * 60));

      if (!isNaN(diffMins) && diffMins >= 0) {
        setFormData((p) => ({ ...p, ttm_duration: diffMins.toString() }));
      }
    } catch (e) {
      console.warn("TTM duration 계산 오류:", e);
    }
  }, [formData.ttm_start_dt, formData.ttm_end_dt]);

  // 스크롤 이벤트 리스너
  useEffect(() => {
    const handleScroll = () => {
      const ecrfContent = document.querySelector(".ecrf-content");
      const scrollTop = ecrfContent ? ecrfContent.scrollTop : window.scrollY;
      setShowScrollButton(scrollTop > 300);
    };

    const ecrfContent = document.querySelector(".ecrf-content");
    const targetElement = ecrfContent || window;

    targetElement.addEventListener("scroll", handleScroll);
    return () => targetElement.removeEventListener("scroll", handleScroll);
  }, []);

  // 맨 위로 스크롤
  const goTop = () => {
    const ecrfContent = document.querySelector(".ecrf-content");

    if (ecrfContent) {
      ecrfContent.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }

    setTimeout(() => {
      if (document.querySelector(".vip-new-case")) {
        document.querySelector(".vip-new-case").focus();
      }
    }, 100);
  };

  // 저장 메시지 표시 컴포넌트
  const SaveMessage = () => {
    if (!saveMessage.text) return null;

    const bgColor = saveMessage.type === "success" ? "#4CAF50" : "#f44336";

    return (
      <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          backgroundColor: bgColor,
          color: "white",
          padding: "12px 24px",
          borderRadius: "4px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
          zIndex: 1000,
          animation: "fadeIn 0.3s ease",
        }}
      >
        {saveMessage.text}
      </div>
    );
  };

  // 성공 팝업 컴포넌트
  const SuccessPopup = () => {
    if (!showSuccessPopup) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1100,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            textAlign: "center",
            minWidth: "300px",
          }}
        >
          <h3 style={{ margin: "0 0 20px 0", color: "#1976D2" }}>저장 완료</h3>
          <p style={{ margin: "0 0 20px 0" }}>
            데이터가 성공적으로 저장되었습니다.
          </p>
          <button
            onClick={() => {
              setShowSuccessPopup(false);
              navigate("/ecrf/subjectlist");
            }}
            style={{
              backgroundColor: "#1976D2",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            확인
          </button>
        </div>
      </div>
    );
  };

  // 저장 핸들러
  const handleSave = async () => {
    if (isSaving) return;

    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });

    try {
      // userValue 체크
      if (userValue === null) {
        throw new Error(
          "사용자 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.",
        );
      }

      // 필수 필드 검증
      if (!formData.hospital) {
        const hospitalLetter = String.fromCharCode(
          64 + Math.floor(userValue / 10),
        );
        console.log(
          "Expected hospital:",
          hospitalLetter,
          "Current:",
          formData.hospital,
        );
        throw new Error(
          `병원 정보가 설정되지 않았습니다. (예상값: ${hospitalLetter})`,
        );
      }

      // 빈 값을 제외한 데이터 객체 생성
      const filteredFormData = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          // 배열인 경우 길이가 0이면 제외
          if (Array.isArray(value) && value.length === 0) {
            return acc;
          }
          // 문자열인 경우 빈 문자열이면 제외
          if (typeof value === "string" && value.trim() === "") {
            return acc;
          }
          // null 또는 undefined면 제외
          if (value === null || value === undefined) {
            return acc;
          }
          // 위 조건을 통과한 경우만 포함
          return { ...acc, [key]: value };
        },
        {},
      );

      // API 호출
      const result = await formAPI.sendVipData(
        filteredFormData,
        projectId,
        formData.caseId || "",
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // 성공 팝업 표시
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("저장 실패:", error);

      // 에러 메시지 표시
      setSaveMessage({
        type: "error",
        text: `저장 실패: ${
          error.message || "알 수 없는 오류가 발생했습니다."
        }`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isSaving) return;

    // 권한 체크 (관리자는 모든 데이터 수정 가능)
    if (!checkPermission()) {
      const message =
        userValue === 999
          ? "관리자 권한으로 모든 데이터를 수정할 수 있습니다."
          : "자신의 기관 데이터만 수정 가능합니다.";

      setSaveMessage({
        type: "error",
        text: message,
      });
      return;
    }

    setIsSaving(true);
    try {
      // 새로운 update API 호출
      const result = await formAPI.updateFormData(dataId, formData);

      if (result.success) {
        setShowSuccessPopup(true);
      } else {
        throw new Error(result.message || "수정 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("수정 실패:", error);
      setSaveMessage({
        type: "error",
        text: "데이터 수정 실패: " + (error.message || "알 수 없는 오류"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  // renderFixedPanel 함수
  const renderFixedPanel = () => {
    // 상태에 따른 배경색 결정 함수
    const getStatusColor = (status) => {
      switch (status) {
        case "Complete":
          return "#4CAF50"; // 초록색
        case "In process":
          return "#FFA726"; // 주황색
        case "Not started":
          return "#EF5350"; // 빨간색
        default:
          return "#9E9E9E"; // 회색
      }
    };

    // 상태 표시 컴포넌트
    const StatusItem = ({ label, status }) => (
      <div
        className="status-item"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          borderBottom: "1px solid #eee",
        }}
      >
        <span
          className="status-label"
          style={{
            fontWeight: "500",
            color: "#333",
            flex: "0 0 200px",
          }}
        >
          {label}:
        </span>
        <span
          className="status-value"
          style={{
            backgroundColor: getStatusColor(status),
            color: "white",
            padding: "4px 12px",
            borderRadius: "12px",
            fontWeight: "bold",
            minWidth: "100px",
            textAlign: "center",
            display: "inline-block",
          }}
        >
          {status || "미입력"}
        </span>
      </div>
    );

    // Additional Daily 상태 아이템들 생성
    const additionalDailyStatuses = () => {
      if (!formData.what_daily) return null;

      const dailyNumbers = formData.what_daily
        .split(", ")
        .map((daily) => daily.replace("Daily", ""));

      return dailyNumbers.map((num) => (
        <StatusItem
          key={`daily${num}`}
          label={`Daily ${num}`}
          status={formData[`d${num}_status`]}
        />
      ));
    };

    return (
      <div
        className={`fixed-bottom-panel ${
          isPanelExpanded ? "expanded" : "collapsed"
        }`}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          zIndex: 1000,
          transition: "all 0.3s ease",
          maxHeight: isPanelExpanded ? "70vh" : "48px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="panel-header"
          onClick={() => setIsPanelExpanded(!isPanelExpanded)}
          style={{
            padding: "12px 24px",
            backgroundColor: "#1976D2",
            color: "white",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "48px",
          }}
        >
          <span
            className="panel-title"
            style={{ fontWeight: "bold", fontSize: "16px" }}
          >
            Status 체크
          </span>
          <span
            className="panel-toggle"
            style={{
              transform: isPanelExpanded ? "rotate(180deg)" : "",
              transition: "transform 0.3s ease",
            }}
          >
            ▼
          </span>
        </div>
        {isPanelExpanded && (
          <div
            className="panel-content"
            style={{
              padding: "0",
              overflowY: "auto",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
              gap: "0",
              maxHeight: "calc(70vh - 48px)",
            }}
          >
            <div
              className="status-grid"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <StatusItem label="Initial" status={formData.initial_status} />
              <StatusItem label="ICU" status={formData.icu_status} />
              <StatusItem label="Daily" status={formData.daily_status} />
              <StatusItem
                label="Additional Daily"
                status={formData.additional_daily_status}
              />
              <StatusItem
                label="Early Outcome"
                status={formData.early_outcome_status}
              />
              <StatusItem
                label="Hospital Discharge"
                status={formData.hosp_discharge_status}
              />
              <StatusItem
                label="Surgeries"
                status={formData.surgeries_status}
              />
              <StatusItem
                label="Final Outcome"
                status={formData.Final_status}
              />
              {additionalDailyStatuses()}
            </div>
          </div>
        )}
      </div>
    );
  };

  // renderStep 함수
  const renderStep = () => {
    console.group("[VIP11Edit] RenderStep");
    console.log("Current Step:", currentStep);
    console.log("Current FormData:", formData);

    // formData가 없으면 로딩 컴포넌트 표시
    if (!formData) {
      console.log("No FormData available, showing LoadingComponent");
      console.groupEnd();
      return <LoadingComponent />;
    }

    let component;
    switch (currentStep) {
      case 1:
        console.log(
          "[VIP11Edit] Rendering InitialPage with formData:",
          formData,
        );
        component = (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <InitialPage
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
        break;
      case 2:
        component = (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <ICUPage
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
        break;
      case 3:
        component = (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <DailyPage
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
        break;
      case 4:
        component = (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <AddDailyPage
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
        break;
      case 5:
        component = (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <EarlyOutcome
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
        break;
      case 6:
        component = (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <HospDischarge
              formData={formData}
              handleChange={handleChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
        break;
      case 7:
        component = (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Surgeries
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
        break;
      case 8:
        component = (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Outcome
              formData={formData}
              handleChange={handleChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
        break;
      default:
        component = (
          <section className="vip-form-section">
            <h2 className="section-title">페이지를 찾을 수 없습니다</h2>
          </section>
        );
        break;
    }
    console.groupEnd();
    return component;
  };

  // StepIndicator 컴포넌트
  const StepIndicator = () => (
    <div className="vip-step-header">
      <div className="vip-step-indicator">
        {stepLabels.map((label, index) => (
          <div
            key={index}
            className={`step-item ${
              currentStep === index + 1 ? "active" : ""
            } ${currentStep > index + 1 ? "completed" : ""}`}
            onClick={() => setCurrentStep(index + 1)}
          >
            <div className="step-number">{index + 1}</div>
            <div className="step-label">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // StepActions 컴포넌트
  const StepActions = () => (
    <div className="vip-step-actions">
      <div className="navigation-buttons">
        <button
          type="button"
          className={`vip-step-button prev ${
            currentStep === 1 ? "disabled" : ""
          }`}
          onClick={() => setCurrentStep((p) => Math.max(1, p - 1))}
          disabled={currentStep === 1}
        >
          <FiChevronLeft size={16} />
          이전
        </button>

        <button
          type="button"
          className={`vip-step-button next ${
            currentStep === totalSteps ? "disabled" : ""
          }`}
          onClick={() => setCurrentStep((p) => Math.min(totalSteps, p + 1))}
          disabled={currentStep === totalSteps}
        >
          다음
          <FiChevronRight size={16} />
        </button>
      </div>

      <div className="save-button-container">
        <button
          type="button"
          className={`vip-submit-button large ${isSaving ? "disabled" : ""}`}
          onClick={handleSubmit}
          disabled={isSaving}
        >
          <FiSave size={16} />
          {isSaving ? "저장 중..." : "저장"}
        </button>
      </div>
    </div>
  );

  // 로딩 상태 변경 시 로깅
  useEffect(() => {
    console.log("[VIP11Edit] Loading state changed:", isLoading);
  }, [isLoading]);

  // 에러 상태 변경 시 로깅
  useEffect(() => {
    if (error) {
      console.error("[VIP11Edit] Error state updated:", error);
    }
  }, [error]);

  // 저장 팝오버 컴포넌트
  const SavePopover = () => {
    if (!showSavePopover) return null;

    return (
      <div
        style={{
          position: "fixed",
          right: "80px",
          top: "170px", // 상단 여백 증가
          backgroundColor: "white",
          padding: "15px",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          zIndex: 1100,
          width: "200px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <span style={{ fontWeight: "bold" }}></span>
          <button
            onClick={() => setShowSavePopover(false)}
            style={{
              border: "none",
              background: "none",
              padding: "4px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            <FiX size={16} />
          </button>
        </div>
        <button
          onClick={() => {
            handleSubmit();
            setShowSavePopover(false);
          }}
          disabled={isSaving}
          style={{
            width: "100%",
            padding: "8px",
            backgroundColor: "#1976D2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <FiSave size={16} />
          {isSaving ? "저장 중..." : "저장하기"}
        </button>
      </div>
    );
  };

  // 떠다니는 저장 아이콘
  const FloatingSaveButton = () => (
    <button
      onClick={() => setShowSavePopover(!showSavePopover)}
      style={{
        position: "fixed",
        right: "20px",
        top: "80px", // 상단 여백 증가
        width: "50px",
        height: "50px",
        borderRadius: "50%",
        backgroundColor: "#1976D2",
        color: "white",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        zIndex: 1000,
      }}
    >
      <FiSave size={24} />
    </button>
  );

  // 권한 없음 메시지 팝업 컴포넌트
  const UnauthorizedPopup = () => {
    const userHospitalCode = userValue
      ? String.fromCharCode(64 + Math.floor(userValue / 10))
      : null;

    if (!userHospitalCode || formData?.hospital === userHospitalCode)
      return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1100,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            textAlign: "center",
            minWidth: "300px",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "20px" }}>⚠️</div>
          <h3 style={{ margin: "0 0 20px 0", color: "#dc3545" }}>
            수정 권한 없음
          </h3>
          <p style={{ margin: "0 0 20px 0" }}>
            자신의 기관 데이터만 수정 가능합니다.
            <br />
            <br />
            현재 데이터: {formData?.hospital || "알 수 없음"} 기관
            <br />
            사용자 소속: {userHospitalCode} 기관
          </p>
          <button
            onClick={() => navigate("/ecrf/subjectlist")}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  };

  // 권한 체크 로직
  const checkPermission = () => {
    if (!userValue || !formData?.hospital) return false;

    // 관리자(999)는 모든 데이터 수정 가능
    if (userValue === 999) return true;

    const userHospitalCode = String.fromCharCode(
      64 + Math.floor(userValue / 10),
    );
    return formData.hospital === userHospitalCode;
  };

  // 권한 없음 메시지 컴포넌트
  const UnauthorizedMessage = () => {
    const userHospitalCode = userValue
      ? String.fromCharCode(64 + Math.floor(userValue / 10))
      : null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1100,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            textAlign: "center",
            minWidth: "300px",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "20px" }}>⚠️</div>
          <h3 style={{ margin: "0 0 20px 0", color: "#dc3545" }}>
            수정 권한 없음
          </h3>
          <p style={{ margin: "0 0 20px 0" }}>
            자신의 기관 데이터만 수정 가능합니다.
            <br />
            <br />
            현재 데이터: {formData?.hospital || "알 수 없음"} 기관
            <br />
            사용자 소속: {userHospitalCode} 기관
          </p>
          <button
            onClick={() => navigate("/ecrf/subjectlist")}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="vip-new-case">
      <SaveMessage />
      <SuccessPopup />
      <FloatingSaveButton />
      <SavePopover />
      <StepIndicator />
      {renderStep()}
      <StepActions />
      {renderFixedPanel()}
      {!checkPermission()}

      {showScrollButton && (
        <button
          onClick={goTop}
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            zIndex: 1000,
            marginBottom: "200px",
          }}
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default VIP11Edit;
